import React, { useEffect, useCallback, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Provider } from 'react-redux';
import { store } from './store';
import theme from './styles/theme';
import { useAppDispatch, useAppSelector } from './hooks/useAppHooks';
import { fetchCurrentUser, logout } from './features/auth/authSlice';
import Login from './components/Auth/Login';
import Layout from './components/Common/Layout';
import Dashboard from './components/Customer/Dashboard';
import RobotList from './components/Customer/RobotList';
import RobotControl from './components/Customer/RobotControl';
import UserManagement from './components/Admin/UserManagement';
import RobotRegistry from './components/Admin/RobotRegistry';
import SystemSettings from './components/Admin/SystemSettings';
import DatabaseExplorer from './components/Admin/DatabaseExplorer';
import MapManagement from './components/Maps/MapManagement';
import MapEditor from './components/Maps/MapEditor';
import SlamViewer from './components/Maps/SlamViewer';
import SecuritySettings from './components/Settings/SecuritySettings';
import ChangePassword from './components/Settings/ChangePassword';
import FleetAnalytics from './components/Admin/FleetAnalytics';
import AuditTrail from './components/Admin/AuditTrail';
import LicenseManagement from './components/Admin/LicenseManagement';
import VoiceAI from './components/Admin/VoiceAI';

// Inactivity timeout in milliseconds (60 minutes - must match backend INACTIVITY_TIMEOUT_MINUTES)
const INACTIVITY_TIMEOUT = 60 * 60 * 1000;

// Warning before logout (5 minutes before timeout)
const WARNING_BEFORE_LOGOUT = 5 * 60 * 1000;

const ProtectedRoute: React.FC<{ children: React.ReactNode; adminOnly?: boolean; superAdminOnly?: boolean }> = ({ children, adminOnly = false, superAdminOnly = false }) => {
  const { isAuthenticated, user, isLoading, accessToken } = useAppSelector((state) => state.auth);
  
  // Show loading if we have a token but user hasn't loaded yet
  if (isLoading || (accessToken && !user)) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#0a0e14', color: '#fff' }}>
        Loading...
      </div>
    );
  }
  
  if (!isAuthenticated || !user) return <Navigate to="/login" replace />;
  if (superAdminOnly && user.role !== 'super_admin') return <Navigate to="/dashboard" replace />;
  if (adminOnly && user.role === 'customer') return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
};

// Hook for tracking user activity and auto-logout
const useInactivityLogout = () => {
  const dispatch = useAppDispatch();
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const warningRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  
  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    
    // Clear existing timers
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (warningRef.current) {
      clearTimeout(warningRef.current);
    }
    
    if (isAuthenticated) {
      // Warning timer
      warningRef.current = setTimeout(() => {
        const remaining = Math.round(WARNING_BEFORE_LOGOUT / 60000);
        console.log(`Session will expire in ${remaining} minutes due to inactivity`);
        // Could show a toast/notification here
      }, INACTIVITY_TIMEOUT - WARNING_BEFORE_LOGOUT);
      
      // Logout timer
      timeoutRef.current = setTimeout(() => {
        console.log('Session expired due to inactivity');
        dispatch(logout());
        window.location.href = '/login?reason=inactivity';
      }, INACTIVITY_TIMEOUT);
    }
  }, [isAuthenticated, dispatch]);
  
  useEffect(() => {
    if (!isAuthenticated) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (warningRef.current) {
        clearTimeout(warningRef.current);
      }
      return;
    }
    
    // Events that indicate user activity
    const activityEvents = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    
    // Throttle the reset to avoid too many updates
    let lastReset = 0;
    const throttledReset = () => {
      const now = Date.now();
      if (now - lastReset > 30000) { // Reset once per 30 seconds
        lastReset = now;
        resetTimer();
      }
    };
    
    // Add event listeners
    activityEvents.forEach(event => {
      window.addEventListener(event, throttledReset, { passive: true });
    });
    
    // Initial timer
    resetTimer();
    
    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, throttledReset);
      });
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (warningRef.current) {
        clearTimeout(warningRef.current);
      }
    };
  }, [isAuthenticated, resetTimer]);
};

const AppContent: React.FC = () => {
  const dispatch = useAppDispatch();
  const { accessToken, user } = useAppSelector((state) => state.auth);
  
  // Use inactivity logout hook
  useInactivityLogout();
  
  // Fetch user on app load if we have a token but no user data
  useEffect(() => {
    if (accessToken && !user) {
      dispatch(fetchCurrentUser());
    }
  }, [accessToken, user, dispatch]);
  
  // Check URL for login reason
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const reason = params.get('reason');
    if (reason === 'inactivity') {
      console.log('You were logged out due to inactivity');
      // Clear the URL parameter
      window.history.replaceState({}, '', window.location.pathname);
    } else if (reason === 'dual-login') {
      console.log('You were logged out because you logged in from another device');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/dashboard" element={<ProtectedRoute><Layout><Dashboard /></Layout></ProtectedRoute>} />
        <Route path="/robots" element={<ProtectedRoute><Layout><RobotList /></Layout></ProtectedRoute>} />
        <Route path="/robots/:robotId/control" element={<ProtectedRoute><Layout><RobotControl /></Layout></ProtectedRoute>} />
        <Route path="/maps" element={<ProtectedRoute><Layout><MapManagement /></Layout></ProtectedRoute>} />
        <Route path="/maps/:mapId/edit" element={<ProtectedRoute><Layout><MapEditor /></Layout></ProtectedRoute>} />
        <Route path="/slam" element={<ProtectedRoute><Layout><SlamViewer /></Layout></ProtectedRoute>} />
        <Route path="/admin/users" element={<ProtectedRoute adminOnly><Layout><UserManagement /></Layout></ProtectedRoute>} />
        <Route path="/admin/robots" element={<ProtectedRoute adminOnly><Layout><RobotRegistry /></Layout></ProtectedRoute>} />
        <Route path="/admin/analytics" element={<ProtectedRoute adminOnly><Layout><FleetAnalytics /></Layout></ProtectedRoute>} />
        <Route path="/admin/audit" element={<ProtectedRoute adminOnly><Layout><AuditTrail /></Layout></ProtectedRoute>} />
        <Route path="/admin/settings" element={<ProtectedRoute superAdminOnly><Layout><SystemSettings /></Layout></ProtectedRoute>} />
        <Route path="/admin/database" element={<ProtectedRoute superAdminOnly><Layout><DatabaseExplorer /></Layout></ProtectedRoute>} />
        <Route path="/security" element={<ProtectedRoute><Layout><SecuritySettings /></Layout></ProtectedRoute>} />
        <Route path="/security/password" element={<ProtectedRoute><Layout><ChangePassword /></Layout></ProtectedRoute>} />
        <Route path="/admin/licenses" element={<ProtectedRoute superAdminOnly><Layout><LicenseManagement /></Layout></ProtectedRoute>} />
        <Route path="/admin/voice-ai" element={<ProtectedRoute superAdminOnly><Layout><VoiceAI /></Layout></ProtectedRoute>} />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </BrowserRouter>
  );
};

const App: React.FC = () => (
  <Provider store={store}>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppContent />
    </ThemeProvider>
  </Provider>
);

export default App;
